import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Subscription } from 'rxjs';

import { BestellungArtikel } from '../../models/bestellposition.model';
import {
  BestellassistentData,
  BestellungenService,
  WeeklyData,
} from '../../services/bestellungen.service';

const EMPTY_ASSISTENT_DATA: BestellassistentData = {
  empfehlung: 0,
  averageSales: 0,
  averageReturns: 0,
  salesRate: 0,
  weeklyData: [],
  weeklyDetails: [],
  vorbestelltMenge: null,
  gesamtBestellt: 0,
  gesamtVerkauft: 0,
  gesamtRetouren: 0,
  gesamtEntlastet: 0,
  trend: 'KeineDaten',
};

@Component({
  selector: 'app-bestellassistent',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './bestellassistent.component.html',
  styleUrl: './bestellassistent.component.scss',
})
export class BestellassistentComponent implements OnDestroy {
  private readonly bestellungenService = inject(BestellungenService);

  private readonly artikelSignal =
    signal<BestellungArtikel | null>(null);

  private readonly bestellungIdSignal =
    signal<string | null>(null);


  private readonly refreshKeySignal =
    signal(0);

  private requestSubscription?: Subscription;
  private loadScheduled = false;

  @Input()
  set artikel(value: BestellungArtikel | null) {
    const previousArtikelNummer =
      this.artikelSignal()?.nummer ?? null;

    const nextArtikelNummer =
      value?.nummer ?? null;

    this.artikelSignal.set(value);

    if (previousArtikelNummer !== nextArtikelNummer) {
      this.scheduleLoadAssistentData();
    }
  }

  @Input()
  set bestellungId(value: string | null) {
    this.bestellungIdSignal.set(value);
    this.scheduleLoadAssistentData();
  }

  @Input()
  set refreshKey(value: number) {
    if (this.refreshKeySignal() === value) {
      return;
    }

    this.refreshKeySignal.set(value);
    this.scheduleLoadAssistentData();
  }

  @Output()
  readonly closed = new EventEmitter<void>();
  @Output()
  readonly recommendationLoaded =
    new EventEmitter<{
      artikelNummer: string;
      menge: number;
    }>();

  protected readonly isLoading =
    signal(false);

  protected readonly loadError =
    signal<string | null>(null);

  protected readonly assistentData =
    signal<BestellassistentData>(
      EMPTY_ASSISTENT_DATA
    );

  protected readonly selectedArtikel =
    computed(() => this.artikelSignal());

  protected readonly currentAssistentData =
    computed(() => this.assistentData());

  protected readonly weeklyData =
    computed(
      () =>
        this.currentAssistentData().weeklyData
    );

  protected readonly weeklyDetails =
    computed(
      () =>
        this.currentAssistentData().weeklyDetails
    );

  protected readonly quantityUnit =
    computed(() => {
      const artikel = this.selectedArtikel();

      if (!artikel) {
        return 'Stück';
      }

      const unit =
        artikel.einheit
          .split('/')[0]
          ?.trim();

      return unit || 'Stück';
    });

  protected readonly vorbestelltInputValue =
    computed<number | null>(() => {
      const vorbestelltMenge =
        this.currentAssistentData()
          .vorbestelltMenge;

      if (vorbestelltMenge === null) {
        return null;
      }

      const artikel =
        this.selectedArtikel();

      if (!artikel) {
        return vorbestelltMenge;
      }

      return this.toInputQuantity(
        vorbestelltMenge,
        artikel.calculationFactor
      );
    });

  protected readonly vorbestelltValue =
    computed(() => {
      const value =
        this.vorbestelltInputValue();

      if (value === null) {
        return '–';
      }

      return this.formatNumber(
        value,
        2
      );
    });

  protected readonly vorbestelltCalculationLabel =
    computed(() => {
      const artikel =
        this.selectedArtikel();

      const actualQuantity =
        this.currentAssistentData()
          .vorbestelltMenge;

      if (
        !artikel ||
        actualQuantity === null ||
        artikel.calculationFactor <= 1
      ) {
        return null;
      }

      const inputQuantity =
        this.toInputQuantity(
          actualQuantity,
          artikel.calculationFactor
        );

      return `${this.formatNumber(
        inputQuantity,
        2
      )} × ${this.formatNumber(
        artikel.calculationFactor,
        2
      )} = ${this.formatNumber(
        actualQuantity,
        2
      )}`;
    });

  protected readonly recommendationInputValue =
    computed(() => {
      const artikel = this.selectedArtikel();

      const actualRecommendation =
        this.currentAssistentData().empfehlung;

      if (!artikel) {
        return actualRecommendation;
      }

      return this.toInputQuantity(
        actualRecommendation,
        artikel.calculationFactor
      );
    });

  protected readonly recommendationValue =
    computed(() =>
      this.formatNumber(
        this.recommendationInputValue(),
        2
      )
    );

  protected readonly recommendationCalculationLabel =
    computed(() => {
      const artikel = this.selectedArtikel();

      if (
        !artikel ||
        artikel.calculationFactor <= 1
      ) {
        return null;
      }

      const inputQuantity =
        this.recommendationInputValue();

      const actualQuantity =
        this.normalizeQuantity(
          inputQuantity *
          artikel.calculationFactor
        );

      return `${this.formatNumber(
        inputQuantity,
        2
      )} × ${this.formatNumber(
        artikel.calculationFactor,
        2
      )} = ${this.formatNumber(
        actualQuantity,
        2
      )}`;
    });

  protected readonly maxWeeklyValue =
    computed(() => {
      const weeklyData = this.weeklyData();

      if (!weeklyData.length) {
        return 1;
      }

      const maxValue = Math.max(
        ...weeklyData.flatMap(
          (week) => [
            week.bestellt,
            week.verkauft,
          ]
        )
      );

      return Math.max(
        1,
        Math.ceil(maxValue / 5) * 5
      );
    });

  protected readonly yAxisValues =
    computed(() => {
      const max = this.maxWeeklyValue();
      const step = max / 4;

      return [
        max,
        max - step,
        max - step * 2,
        max - step * 3,
        0,
      ].map((value) =>
        Math.round(value)
      );
    });

  protected readonly averageSales =
    computed(() =>
      this.formatNumber(
        this.currentAssistentData()
          .averageSales,
        1
      )
    );

  protected readonly salesRate =
    computed(
      () =>
        this.currentAssistentData()
          .salesRate
    );

  protected readonly averageSoldLine =
    computed(() => {
      const weeklyData = this.weeklyData();

      if (!weeklyData.length) {
        return 0;
      }

      const total = weeklyData.reduce(
        (sum, week) =>
          sum + week.verkauft,
        0
      );

      return total / weeklyData.length;
    });

  protected readonly averageSoldLinePosition =
    computed(() =>
      Math.max(
        0,
        Math.min(
          100,
          (this.averageSoldLine() /
            this.maxWeeklyValue()) *
          100
        )
      )
    );

  protected readonly averageRetoure =
    computed(() =>
      this.formatNumber(
        this.currentAssistentData()
          .averageReturns,
        1
      )
    );

  protected readonly totalRetouren =
    computed(() =>
      this.formatNumber(
        this.currentAssistentData()
          .gesamtRetouren,
        1
      )
    );

  protected readonly totalEntlastet =
    computed(() =>
      this.formatNumber(
        this.currentAssistentData()
          .gesamtEntlastet,
        1
      )
    );

  protected readonly trendLabel =
    computed(() => {
      switch (
      this.currentAssistentData().trend
      ) {
        case 'Steigend':
          return '↑ Steigender Trend';

        case 'Sinkend':
          return '↓ Sinkender Trend';

        case 'Stabil':
          return '→ Stabiler Trend';

        case 'KeineDaten':
        default:
          return 'Trend nicht verfügbar';
      }
    });

  protected readonly activeArtikelNumber =
    computed(
      () =>
        this.selectedArtikel()?.nummer ??
        null
    );

  ngOnDestroy(): void {
    this.requestSubscription?.unsubscribe();
  }

  protected close(): void {
    this.closed.emit();
  }

  protected getSoldBarClass(
    index: number
  ): string {
    if (index === 0) {
      return 'sold-neutral';
    }

    const weeklyData = this.weeklyData();

    const currentSold =
      weeklyData[index]?.verkauft ?? 0;

    const previousSold =
      weeklyData[index - 1]?.verkauft ??
      0;

    return currentSold >= previousSold
      ? 'sold-positive'
      : 'sold-negative';
  }

  protected getBarHeight(
    week: WeeklyData,
    key: 'bestellt' | 'verkauft'
  ): number {
    return Math.max(
      4,
      (week[key] /
        this.maxWeeklyValue()) *
      100
    );
  }

  private scheduleLoadAssistentData(): void {
    if (this.loadScheduled) {
      return;
    }

    this.loadScheduled = true;

    queueMicrotask(() => {
      this.loadScheduled = false;
      this.loadAssistentData();
    });
  }

  private loadAssistentData(): void {
    const artikel =
      this.artikelSignal();

    const bestellungId =
      this.bestellungIdSignal();

    this.requestSubscription?.unsubscribe();

    if (!artikel || !bestellungId) {
      this.assistentData.set(
        EMPTY_ASSISTENT_DATA
      );

      this.isLoading.set(false);
      this.loadError.set(null);

      return;
    }

    const artikelnummer =
      Number(artikel.nummer);

    if (Number.isNaN(artikelnummer)) {
      this.assistentData.set(
        EMPTY_ASSISTENT_DATA
      );

      this.isLoading.set(false);

      this.loadError.set(
        'Artikelnummer ist ungültig.'
      );

      return;
    }

    this.isLoading.set(true);
    this.loadError.set(null);

    this.requestSubscription =
      this.bestellungenService
        .getBestellassistentData(
          bestellungId,
          artikelnummer
        )
        .subscribe({
          next: (data) => {
            this.assistentData.set(data);

            const artikel =
              this.artikelSignal();

            if (!artikel) {
              return;
            }

            this.recommendationLoaded.emit({
              artikelNummer: artikel.nummer,

              menge: this.toInputQuantity(
                data.empfehlung,
                artikel.calculationFactor
              ),
            });
          },

          error: () => {
            this.assistentData.set(
              EMPTY_ASSISTENT_DATA
            );

            this.loadError.set(
              'Bestellassistent-Daten konnten nicht geladen werden.'
            );

            this.isLoading.set(false);
          },

          complete: () => {
            this.isLoading.set(false);
          },
        });
  }

  private toInputQuantity(
    actualQuantity: number,
    calculationFactor: number
  ): number {
    if (calculationFactor <= 1) {
      return this.normalizeQuantity(
        actualQuantity
      );
    }

    return this.normalizeQuantity(
      actualQuantity /
      calculationFactor
    );
  }

  private normalizeQuantity(
    value: number
  ): number {
    return Number(value.toFixed(6));
  }

  private formatNumber(
    value: number,
    maximumFractionDigits: number
  ): string {
    return new Intl.NumberFormat(
      'de-DE',
      {
        maximumFractionDigits,
      }
    ).format(value);
  }
}